import { useEffect, useRef } from "react";
import "@/App.css";
import { motion, useInView } from "framer-motion";

// Logo URLs
const LOGO_HEADER_SVG = "/rise-header.svg";
const LOGO_FOOTER_SVG = "/rise-footer.svg";
const LOGO_ROUND = "https://customer-assets.emergentagent.com/job_rise-goldcoast/artifacts/i8yqrulb_8.png";
const LOGO_HERO = "/rise-hero-logo.svg";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Reusable animated section wrapper
const AnimatedSection = ({ children, className = "", id = "", "data-testid": testId }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      className={className}
      data-testid={testId}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
    >
      {children}
    </motion.section>
  );
};

// Core Values Data
const coreValues = [
  {
    number: 1,
    title: "On Purpose",
    description: "We are at our best when we're aligned with our purpose - operating from joy, clarity and conviction, delivering excellence in all we do."
  },
  {
    number: 2,
    title: "Honest to our Core",
    description: "We practise total honesty - placing truth, transparency, and ethics at the centre of every decision."
  },
  {
    number: 3,
    title: "Compassion & Respect",
    description: "We treat employees, volunteers, clients, partners and communities with kindness, dignity, and fairness, ensuring that everyone feels valued, seen and heard."
  },
  {
    number: 4,
    title: "Excellent Stewardship",
    description: "We take responsibility for the resources, time, influence and opportunities entrusted to us, striving for the highest standards of stewardship."
  },
  {
    number: 5,
    title: "Servant Leadership",
    description: "We lead ourselves first, then others with humility and courage, putting people before position, and fostering a culture of encouragement, support and empowerment."
  },
  {
    number: 6,
    title: "Honouring Identity",
    description: "We recognise and honor the unique design, contributions, and strengths of both men and women, embracing their unique value and diversity to the organisation and community."
  }
];

// Core Values Page Component
const CoreValuesPage = () => {
  useEffect(() => {
    document.title = "Our Core Values | Project RISE";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="CoreValuesPage" data-testid="core-values-page">
      {/* Navigation */}
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-28 lg:pb-24 bg-[#5A99D4]/20" data-testid="core-values-hero">
        <div className="rise-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl"
          >
            {/* Logo */}
            <img 
              src={LOGO_HERO} 
              alt="Project RISE" 
              className="h-[4rem] lg:h-[6rem] w-auto object-contain block mb-8"
              data-testid="core-values-logo"
            />
            
            <h1 className="heading-hero text-black mb-8" data-testid="core-values-title">
              Our Core Values
            </h1>
            
            <div className="space-y-4">
              <p className="body-large text-black/80">
                At RISE, our core values are the foundation of everything we do. They guide our actions, shape our culture, and ensure that we remain aligned with our purpose, creating lasting positive change in individuals and communities.
              </p>
              <p className="body-large text-black/80">
                These values foster trust, integrity, and accountability, ensuring that every decision we make and every initiative we undertake reflects our commitment to empowering communities and making a lasting impact.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Core Values Grid */}
      <section className="py-16 lg:py-24 bg-white" data-testid="core-values-grid">
        <div className="rise-container">
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {coreValues.map((value) => (
              <motion.div 
                key={value.number}
                variants={fadeInUp}
                className="core-value-card bg-[#FAF2E6] rounded-2xl p-8 lg:p-10"
                data-testid={`core-value-${value.number}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-4xl lg:text-5xl font-montserrat font-bold text-[#5A99D4]">
                    {value.number}
                  </span>
                  <div className="h-[2px] flex-1 bg-[#5A99D4]/30" />
                </div>
                <h3 className="font-montserrat font-bold text-xl lg:text-2xl text-black mb-4">
                  {value.title}
                </h3>
                <p className="body-regular text-[#737373]">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      
      {/* Call to Action */}
      <AnimatedSection className="py-16 lg:py-24 bg-[#5A99D4]/20" data-testid="core-values-cta">
        <div className="rise-container text-center">
          <h2 className="heading-section text-black mb-6">
            Ready to learn more?
          </h2>
          <p className="body-large text-[#737373] max-w-2xl mx-auto mb-8">
            Discover how RISE is making a difference across mental health, domestic violence, vulnerable communities, and homelessness.
          </p>
          <a 
            href="/#focus-areas" 
            className="btn-primary-rounded inline-block"
            data-testid="explore-focus-areas-btn"
          >
            Explore Our Focus Areas
          </a>
        </div>
      </AnimatedSection>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

// Navigation Component - Transparent floating nav over hero
const Navigation = () => {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-transparent" data-testid="navigation">
      <div className="rise-container">
        <div className="flex items-center h-16 md:h-20 py-3 md:py-0">
          {/* Navigation Links - Floating transparent text */}
          <div className="flex items-center gap-4 md:gap-8 flex-wrap">
            <a 
              href="/" 
              className="font-poppins font-semibold text-xs md:text-sm text-black hover:text-black/70 transition-colors py-2"
              data-testid="nav-home"
            >
              Home
            </a>
            <a 
              href="/core-values" 
              className="font-poppins font-semibold text-xs md:text-sm text-black hover:text-black/70 transition-colors py-2"
              data-testid="nav-core-values"
            >
              Our Core Values
            </a>
            <a 
              href="/#focus-areas" 
              className="font-poppins font-semibold text-xs md:text-sm text-black hover:text-black/70 transition-colors py-2"
              data-testid="nav-focus-areas"
            >
              Our Focus Areas
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  const links = [
    { label: "Home", href: "/" },
    { label: "Core Values", href: "/core-values" },
    { label: "Focus Areas", href: "/#focus-areas" },
    { label: "Contact", href: "#contact" }
  ];

  return (
    <footer id="contact" className="py-12 md:py-16 bg-black border-t border-white/10 relative overflow-hidden" data-testid="footer">
      {/* Subtle white logo watermark */}
      <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 pointer-events-none hidden md:block">
        <img 
          src={LOGO_ROUND} 
          alt="" 
          className="w-64 h-64 object-contain opacity-[0.03] brightness-0 invert"
          aria-hidden="true"
        />
      </div>
      
      <div className="rise-container relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          <div className="text-center md:text-left">
            <img 
              src={LOGO_FOOTER_SVG} 
              alt="Project RISE" 
              className="block mb-6 md:mb-8 w-[120px] h-[120px] md:w-[150px] md:h-[150px] object-contain mx-auto md:mx-0"
              data-testid="footer-logo"
            />
            <p className="body-regular text-[#737373] max-w-sm mx-auto md:mx-0">
              Reach. Innovate. Support. Empower.<br />
              Gold Coast, Australia
            </p>
          </div>
          
          <div className="flex flex-wrap gap-4 md:gap-8 justify-center md:justify-end items-start" data-testid="footer-links">
            {links.map((link, index) => (
              <a 
                key={index}
                href={link.href}
                className="caption text-[#737373] hover:text-white transition-colors"
                data-testid={`footer-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
        
        <div className="mt-10 md:mt-16 pt-6 md:pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
          <p className="text-xs md:text-sm text-[#737373]" data-testid="copyright">
            &copy; {new Date().getFullYear()} Project RISE. All rights reserved.
          </p>
          <div className="flex items-center gap-4 md:gap-6">
            <p className="text-xs md:text-sm text-[#737373]" data-testid="launching">
              Launching 2026
            </p>
            <img 
              src={LOGO_ROUND} 
              alt="" 
              className="w-8 h-8 object-contain opacity-30 brightness-0 invert hidden md:block"
              aria-hidden="true"
            />
          </div>
        </div>
        
        {/* AI Imagery Disclaimer */}
        <p className="mt-8 text-center text-[11px] text-[#737373] font-poppins" data-testid="ai-disclaimer">
          Some imagery on this website has been AI generated for illustrative purposes and to protect the privacy and dignity of individuals represented.
        </p>
      </div>
    </footer>
  );
};

export { CoreValuesPage, Navigation, Footer };
